import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { customersApi } from "@/lib/admin-api/crm";
import { ApiError } from "@/lib/api-error-handler";

// ---------------------------------------------------------------------------
// BlockPanel — surfaces block status + lets admins block/unblock a customer
// ---------------------------------------------------------------------------

export function BlockPanel({ customerId }: { readonly customerId: string }) {
  const queryClient = useQueryClient();
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reason, setReason] = useState("");

  const { data: detail } = useQuery({
    queryKey: ["admin", "customer-detail", customerId],
    queryFn: () => customersApi.getDetail(customerId),
  });

  const blockMutation = useMutation({
    mutationFn: (input: { is_blocked: boolean; blocked_reason?: string }) =>
      customersApi.updateBlock(customerId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "customer-detail", customerId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "customers"] });
      toast.success(variables.is_blocked ? "Client bloqué" : "Client débloqué");
      setReasonModalOpen(false);
      setReason("");
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.toHumanMessage());
      } else {
        toast.error("Impossible de mettre à jour le statut");
      }
    },
  });

  if (!detail) return null;

  const isBlocked = detail.is_blocked === true;

  return (
    <div
      className={`rounded-xl border p-5 ${
        isBlocked
          ? "border-red-200 bg-red-50/50"
          : "border-stone-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {isBlocked ? (
            <Ban className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          ) : (
            <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          )}
          <div>
            <h3 className="text-sm font-semibold text-stone-800">
              {isBlocked ? "Client bloqué" : "Client actif"}
            </h3>
            {isBlocked && detail.blocked_reason && (
              <p className="mt-0.5 text-xs text-red-700">
                Raison : {detail.blocked_reason}
              </p>
            )}
            {isBlocked && detail.blocked_at && (
              <p className="mt-0.5 text-xs text-stone-500">
                Depuis le {new Date(detail.blocked_at).toLocaleDateString("fr-SN")}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          disabled={blockMutation.isPending}
          onClick={() => {
            if (isBlocked) {
              if (confirm("Débloquer ce client ?")) {
                blockMutation.mutate({ is_blocked: false });
              }
            } else {
              setReasonModalOpen(true);
            }
          }}
          className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
            isBlocked
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
          }`}
        >
          {isBlocked ? "Débloquer" : "Bloquer"}
        </button>
      </div>

      {reasonModalOpen && (
        <div className="mt-4 space-y-2 rounded-lg border border-red-200 bg-white p-3">
          <label className="text-xs font-medium text-stone-700" htmlFor="block-reason">
            Raison du blocage (obligatoire, min. 3 caractères)
          </label>
          <textarea
            id="block-reason"
            className="w-full rounded border border-stone-200 bg-white px-2 py-1 text-sm focus:border-amber-400 focus:outline-none"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: Commandes frauduleuses répétées"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={blockMutation.isPending || reason.trim().length < 3}
              onClick={() =>
                blockMutation.mutate({
                  is_blocked: true,
                  blocked_reason: reason.trim(),
                })
              }
              className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Confirmer le blocage
            </button>
            <button
              type="button"
              onClick={() => {
                setReasonModalOpen(false);
                setReason("");
              }}
              className="rounded border border-stone-200 px-3 py-1 text-xs text-stone-600 hover:bg-stone-50"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
